import { ReactNode } from 'react';
import cx from 'classnames';
import { Column } from 'primereact/column';
import { DataTable, DataTableProps } from 'primereact/datatable';
import { ColumnDescription } from './ColumnDescription';
import { CustomPaginator } from './CustomPaginator';
import { DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { useTranslation } from '@/config/i18n';

type Props = DataTableProps<any> & {
  values: any[];
  selectedValues?: any[];
  columns: ColumnDescription[];
  pagination?: Pagination;
  selectable?: boolean;
  onSortChanged?: (paginationState: Pagination) => void;
  onPaginationChanged?: (paginationState: Pagination) => void;
  onSelectionChange?: (values: any[]) => void;
  className?: string;
  wrapClassName?: string;
  extraFooter?: ReactNode;
  scrollable?: boolean;
};

export const CustomDataTable: React.FC<Props> = ({
  values,
  selectedValues,
  columns,
  pagination,
  selectable,
  onSortChanged,
  onPaginationChanged,
  onSelectionChange,
  className,
  wrapClassName,
  extraFooter,
  scrollable = false,
  reorderableRows,
  emptyMessage,
  ...rest
}) => {
  const [t] = useTranslation('');
  const onSort = (event: any) => {
    onSortChanged?.({
      limit: pagination?.limit,
      total_page: pagination?.total_page,
      paginate: pagination?.paginate,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    });
  };

  const onSelectChange = (event: any) => {
    onSelectionChange?.(event.value);
  };

  return (
    <div className={cx('table-custom flex flex-col', wrapClassName)}>
      <DataTable
        className={className}
        first={((pagination?.paginate ?? 0) - 1) * (pagination?.limit ?? 0)}
        rows={pagination?.limit ?? DEFAULT_PER_PAGE}
        totalRecords={pagination?.total_page ?? values.length}
        lazy
        onSort={onSort}
        sortField={pagination?.sortField ?? undefined}
        sortOrder={pagination?.sortOrder}
        value={values}
        stripedRows
        removableSort
        tableStyle={{ minWidth: '50rem' }}
        selection={selectedValues}
        selectAll={values == selectedValues}
        onSelectionChange={onSelectChange}
        // Destructured on purpose rather than left to the rest spread. It
        // reads the same either way today, but a caller passing an explicit
        // `undefined` through the spread would wipe the default out, and a
        // later refactor that destructured it without a fallback would drop
        // every caller's text with no type error to show for it.
        emptyMessage={emptyMessage ?? t('common.table.empty')}
        scrollable={scrollable}
        reorderableRows={reorderableRows}
        selectionMode={selectable ? 'multiple' : undefined}
        {...rest}
      >
        {selectable && (
          <Column
            selectionMode="multiple"
            style={{
              minWidth: '2rem',
              textAlign: 'center',
            }}
          />
        )}

        {reorderableRows && (
          <Column
            rowReorder
            style={{
              width: '40px',
              paddingRight: 0,
            }}
            rowReorderIcon="pi pi-drag-drop mt-[5px]"
          />
        )}

        {columns.map((column: ColumnDescription, index) => {
          return (
            <Column
              sortable={column.sort}
              key={index}
              className={column.classname ?? undefined}
              headerClassName={column.headerClassname ?? undefined}
              header={column.text ?? column.header}
              field={column.dataField}
              body={column.formatter ?? undefined}
              frozen={column.frozen ?? undefined}
              sortField={column.sortField ?? column.dataField}
            />
          );
        })}
      </DataTable>
      {extraFooter && (extraFooter)}
      {pagination && (
        <div>
          <CustomPaginator
            pagination={pagination}
            onPaginationChanged={onPaginationChanged}
          />
        </div>
      )}
    </div>
  );
};
